import {
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export enum Themes {
  light = "light",
  dark = "dark",
  system = "system",
}

export const themeChangeEvent = new Event("themeChange");

const getSystemTheme = (): Themes =>
  window.matchMedia("(prefers-color-scheme: dark)").matches
    ? Themes.dark
    : Themes.light;

const getStoredTheme = (): Themes => {
  const localTheme = localStorage.getItem("theme");
  return Object.values(Themes).includes(localTheme as Themes)
    ? (localTheme as Themes)
    : Themes.system;
};

export const getTheme = (): Themes => {
  const theme = getStoredTheme();
  if (theme === Themes.system) {
    return getSystemTheme();
  } else {
    return theme;
  }
};

export default function ThemeSelector() {
  const { t } = useTranslation("ThemeSelector");
  const [selectedMode, setSelectedMode] = useState<Themes>(getStoredTheme());
  const [theme, setTheme] = useState<Themes>(
    selectedMode === Themes.system ? getSystemTheme() : selectedMode,
  );

  const applyTheme = (newTheme: Themes) => {
    document.querySelector("html")?.setAttribute("data-theme", newTheme);
    window.dispatchEvent(themeChangeEvent);
  };

  const handleThemeModeChange = (mode: Themes) => {
    setSelectedMode(mode);
    localStorage.setItem("theme", mode);
    const newTheme = mode === Themes.system ? getSystemTheme() : mode;
    setTheme(newTheme);
    applyTheme(newTheme);
  };

  useEffect(() => {
    const systemThemeMediaQuery = window.matchMedia(
      "(prefers-color-scheme: dark)",
    );

    const handleSystemChange = (e: MediaQueryListEvent) => {
      if (selectedMode === Themes.system) {
        const newTheme = e.matches ? Themes.dark : Themes.light;
        setTheme(newTheme);
        applyTheme(newTheme);
      }
    };

    if (selectedMode === Themes.system) {
      systemThemeMediaQuery.addEventListener("change", handleSystemChange);
    }

    return () => {
      systemThemeMediaQuery.removeEventListener("change", handleSystemChange);
    };
  }, [selectedMode]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return (
    <div className="dropdown dropdown-end">
      <div
        // biome-ignore lint/a11y/useSemanticElements: Fix for Safari Bug https://daisyui.com/components/dropdown/#method-2-using-css-focus
        tabIndex={0}
        role="button"
        className="btn btn-ghost btn-sm btn-circle"
      >
        {theme === Themes.light ? (
          <SunIcon className="h-6 w-6" />
        ) : (
          <MoonIcon className="h-6 w-6" />
        )}
      </div>
      <ul
        // biome-ignore lint/a11y/noNoninteractiveTabindex: Fix for Safari Bug https://daisyui.com/components/dropdown/#method-2-using-css-focus
        tabIndex={0}
        className="dropdown-content menu z-[1] w-52 gap-1 rounded-box bg-neutral p-2 shadow"
      >
        {[Themes.light, Themes.dark, Themes.system].map((mode) => (
          <li key={mode}>
            <button
              type="button"
              onClick={() => handleThemeModeChange(mode)}
              className={selectedMode === mode ? "bg-base-300" : ""}
            >
              {mode === Themes.light && (
                <SunIcon className="mr-2 inline-block h-5 w-5" />
              )}
              {mode === Themes.dark && (
                <MoonIcon className="mr-2 inline-block h-5 w-5" />
              )}
              {mode === Themes.system && (
                <ComputerDesktopIcon className="mr-2 inline-block h-5 w-5" />
              )}
              {t(mode)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
